import * as XLSX from "xlsx";
import * as XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import { IoDocumentOutline } from "react-icons/io5";

import Toaster from "../../../components/common/Toaster";
import taxService from "../../../services/taxService";
import moment from "moment";

export const ReceiptExport = ({ id, account, data, options }) => {
  const fetchData = async () => {
    try {
      const total_count = data.length > 0 ? data[0].total_count : options.size;
      const params = { ...options };
      params["size"] = total_count;

      const { result } = await taxService.getAccountReceiptsAsync(id, params);

      const items = result.map((item) => {
        const {
          date_issued,
          quarter,
          number,
          client_name,
          gross_amount,
          discount_amount,
          net_receipt,
          withheld_amount,
          net_amount,
          tax_rate,
        } = item;

        const result = {};
        result["number"] = number;
        result["client_name"] = client_name;
        result["quarter"] = quarter;
        result["gross_amount"] = gross_amount;
        result["discount_amount"] = discount_amount;
        result["net_receipt"] = net_receipt;
        result["withheld_amount"] = withheld_amount;
        result["net_amount"] = net_amount;
        result["tax_rate"] = `${tax_rate}%`;
        result["month"] = date_issued ? moment(date_issued).format("MMM") : "";
        result["date"] = date_issued
          ? moment(date_issued).format("DD-MMM-YYYY")
          : "";

        return result;
      });

      return items;
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  const workbook2blob = (workbook) => {
    const wopts = {
      bookType: "xlsx",
      bookSST: false,
      type: "binary",
    };

    const wbout = XLSX.write(workbook, wopts);

    const blob = new Blob([s2ab(wbout)], {
      type: "application/octet-stream",
    });

    return blob;
  };

  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i !== s.length; ++i) {
      view[i] = s.charCodeAt(i);
    }

    return buf;
  };

  const handleExport = async () => {
    const receipts = await fetchData();

    const table = [
      {
        A: "MONTH",
        B: "QUARTER",
        C: "DATE",
        D: "OR #",
        E: "PARTICULARS",
        F: "GROSS AMOUNT",
        G: "DISCOUNT",
        H: "NET RECEIPTS",
        I: "WITHHELD AMOUNT",
        J: "NET AMOUNT",
        K: "RATE",
      },
    ];

    receipts.forEach((item) => {
      table.push({
        A: item.month,
        B: item.quarter,
        C: item.date,
        D: item.number,
        E: item.client_name,
        F: item.gross_amount,
        G: item.discount_amount,
        H: item.net_receipt,
        I: item.withheld_amount,
        J: item.net_amount,
        K: item.tax_rate,
      });
    });

    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(table, {
      skipHeader: true,
    });

    XLSX.utils.book_append_sheet(workbook, sheet, "OR");

    const workbookBlob = workbook2blob(workbook);
    const total = receipts.length + 1;

    const info = {
      bodyRange: `A1:K${table.length}`,
      headRange: `A1:K1`,
      leftColumns: `E2:E${total}`,
      rightColumns: `F2:K${total}`,
    };

    return addStyle(workbookBlob, info, total);
  };

  const addStyle = (workbookBlob, info, total) => {
    return XlsxPopulate.fromDataAsync(workbookBlob).then((workbook) => {
      workbook.sheets().forEach((sheet) => {
        sheet.usedRange().style({
          fontFamily: "Calibri",
          verticalAlignment: "center",
        });

        const getMaxStringLength = (range) => {
          return sheet.range(range).reduce((max, cell) => {
            const value = cell.value();
            if (value === undefined) return max;
            return Math.max(max, value.toString().length);
          }, 0);
        };

        sheet.column("C").width(14);
        sheet.column("E").width(getMaxStringLength(`E1:E${total}`));
        sheet.column("F").width(16);
        sheet.column("G").width(12);
        sheet.column("H").width(14);
        sheet.column("I").width(20);
        sheet.column("J").width(14);

        sheet.range(info.headRange).style({
          fill: "808080",
          horizontalAlignment: "center",
          fontColor: "ffffff",
        });

        sheet.range(info.bodyRange).style({
          horizontalAlignment: "center",
        });

        sheet.range(info.leftColumns).style({
          horizontalAlignment: "left",
        });

        sheet.range(info.rightColumns).style({
          horizontalAlignment: "right",
          numberFormat: "#,###",
        });
      });

      return workbook
        .outputAsync()
        .then((workbookBlob) => URL.createObjectURL(workbookBlob));
    });
  };

  const createDownloadData = () => {
    handleExport().then((url) => {
      const year = options.year ? ` ${options.year}` : "";
      const quarters = taxService.getAltTaxQuarters();
      const quarter = options.quarter
        ? ` ${quarters.filter((i) => i.value === options.quarter)[0].label}`
        : "";

      const file_name = `OR - ${account.short_name}${year}${quarter}`;

      const downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", file_name);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    });
  };

  return (
    <div>
      <button
        title="Export to Excel"
        className="button cursor-pointer h-10 border group hover:border-blue-500"
        onClick={() => createDownloadData()}
      >
        <IoDocumentOutline className="text-gray-400 group-hover:text-blue-500" />
      </button>
    </div>
  );
};

export default ReceiptExport;
